import {
  Activity,
  ChartBreakoutSquare,
  ClockSnooze,
  CurrencyPoundCircle,
  PlaySquare,
  ShieldTick,
  Tag01,
  Trophy01,
} from "@untitledui/icons";
import type { BrandCreatorClassification } from "@/api/creator-metrics";
import { getCreatorSafetyStatus } from "@/utils/creator-safety";
import { cx } from "@/utils/cx";

type ClassificationConfig = {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
};

const CLASSIFICATION_CONFIG: Record<BrandCreatorClassification, ClassificationConfig> = {
  top_creator: {
    icon: <Trophy01 className="w-5 h-5" />,
    title: "Top Performers",
    description:
      "Drives high GMV with consistent, high-quality posts. Reliable, low-risk, and fully aligned with your brand.",
    gradient: "bg-gradient-to-r from-lime-200/20 to-green-500/20",
  },
  active_partner: {
    icon: <Activity className="w-5 h-5" />,
    title: "Active Partner",
    description:
      "Strong results and brand fit. Steady content and good GMV, dependable collaborator for continued growth.",
    gradient: "bg-gradient-to-r from-indigo-200/20 to-indigo-500/20",
  },
  rising_star: {
    icon: <ChartBreakoutSquare className="w-5 h-5" />,
    title: "Rising Star",
    description:
      "New to your brand but rapidly gaining traction. High early GMV and strong posting momentum, one to watch closely.",
    gradient: "bg-gradient-to-r from-cyan-200/20 to-cyan-500/20",
  },
  dormant_creator: {
    icon: <ClockSnooze className="w-5 h-5" />,
    title: "Dormant Partner",
    description:
      "Once high-performing but recently inactive. Still a good brand fit, needs re-engagement to revive sales.",
    gradient: "bg-gradient-to-r from-pink-200/20 to-pink-500/20",
  },
};

type StatItem = {
  icon: React.ReactNode;
  label: string;
  value?: string;
};

function getPostingLabel(avgVideos: number): string {
  if (avgVideos >= 3) return "Frequent posting";
  if (avgVideos >= 2) return "Regular posting";
  if (avgVideos >= 1) return "Low activity";
  return "Irregular posting";
}

type ClassificationCardProps = {
  classification: BrandCreatorClassification;
  topCategory?: string;
  avgVideos: number;
  avgGMV: number;
  /** Number of the creator's videos flagged with a tier 1 (most severe) risk. */
  videosWithTier1: number;
  /** Percentage (0–100) of the creator's videos flagged with a tier 2 risk. */
  percentageTier2AtRisk: number;
  currencyFormatter: Intl.NumberFormat;
  /** When true, hides the title/description header and shows only stats */
  compact?: boolean;
};

export const ClassificationCard = ({
  classification,
  topCategory,
  avgVideos,
  avgGMV,
  videosWithTier1,
  percentageTier2AtRisk,
  currencyFormatter,
  compact = false,
}: ClassificationCardProps) => {
  const config = CLASSIFICATION_CONFIG[classification];

  const stats: StatItem[] = [
    ...(topCategory
      ? [
          {
            icon: <Tag01 className="w-5 h-5 text-primary" />,
            label: `${topCategory} content`,
          },
        ]
      : []),
    {
      icon: <PlaySquare className="w-5 h-5 text-primary" />,
      label: `${getPostingLabel(avgVideos)} (${Math.round(avgVideos)}/mo)`,
    },
    {
      icon: <CurrencyPoundCircle className="w-5 h-5 text-primary" />,
      label: `Averages ${currencyFormatter.format(avgGMV * avgVideos)}/month GMV`,
    },
    {
      icon: <ShieldTick className="w-5 h-5 text-primary" />,
      label: getCreatorSafetyStatus({ videosWithTier1, percentageTier2AtRisk }).label,
    },
  ];

  if (compact) {
    return (
      <div className={cx("rounded-lg px-5 py-5 flex flex-col gap-3", config.gradient)}>
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center gap-2">
            {stat.icon}
            <span className="text-sm text-primary leading-5">{stat.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cx("rounded-lg px-10 py-8 flex justify-between items-start gap-8", config.gradient)}>
      {/* Left side - Icon, Title, Description */}
      <div className="flex flex-col gap-4 max-w-80">
        <div className="text-primary">{config.icon}</div>
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-medium text-primary leading-7">{config.title}</h3>
          <p className="text-sm text-secondary leading-5">{config.description}</p>
        </div>
      </div>

      {/* Right side - Stats */}
      <div className="flex flex-col gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center gap-1.5">
            {stat.icon}
            <span className="text-sm text-primary leading-5">{stat.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
